/**
 * Gráficas
 * -----------------------------------------------------------------------------
 * Una serie de datos en un `<canvas>`, sin escribir JavaScript en la aplicación:
 *
 *   <div class="company-grafica" data-company-grafica
 *        data-company-grafica-tipo="line"
 *        data-company-grafica-etiquetas='["2026-08-25","2026-08-26"]'
 *        data-company-grafica-series='[{"nombre":"Precio interno","datos":[2200000,2210000]}]'>
 *     <canvas></canvas>
 *   </div>
 *
 * Los datos van en atributos y no en un guion suelto por lo de siempre: escribir
 * `new Chart(...)` en cada plantilla acaba en cinco pantallas con cinco paletas,
 * cinco formatos de número y cinco maneras de fallar cuando no hay datos.
 *
 * ## Chart.js **no viaja en el paquete**
 *
 * Son 500 kB. Dentro del paquete lo descargaría todo el mundo en cada página de
 * la suite para nada: hoy hay una pantalla con gráfica. Se trae **al montar la
 * primera gráfica**, igual que el lector de QR. Ver `graficas()` en
 * `vite.config.js`, que es el mismo mecanismo y explica por qué son dos ficheros
 * -el de MDB es un módulo ES y hace falta un shim que cuelgue `Chart` del
 * global-.
 *
 * ## Los colores salen del tema, no de Chart.js
 *
 * Se leen de las variables CSS. Así una gráfica en modo oscuro no se queda con
 * los azules de fábrica sobre fondo negro, y cambiar la marca no obliga a tocar
 * JavaScript.
 *
 * ## Sin datos se dice, no se pinta un cuadro vacío
 *
 * Un lienzo en blanco con ejes se lee como «el valor es cero», que es distinto de
 * «todavía no hay nada». Es el mismo error que la tabla que enseña «0 resultados»
 * teniendo filas, y aquí es peor porque un cero en una serie de precios parece un
 * dato.
 */

const ATTR = 'data-company-grafica';

/** Cuántos puntos se enseñan antes de empezar a saltarse etiquetas del eje. */
const ETIQUETAS_MAXIMAS = 12;

let cargando = null;

/**
 * Chart.js, traído solo cuando hay una gráfica que pintar.
 *
 * @returns {Promise<?Function>}
 */
function traerChart() {
  if (window.Chart) return Promise.resolve(window.Chart);
  if (cargando) return cargando;

  // Junto al guion del tema y con su mismo sello de versión, exactamente igual
  // que el lector de QR: cada aplicación publica el tema en su sitio y aquí no
  // se sabe cuál es, y sin el `?v=` el navegador se queda con el que ya tenía.
  const suyo = document.querySelector('script[src*="company-theme"]');
  const base = suyo ? suyo.src.replace(/[^/]+$/, '') : '';
  const sello = suyo && suyo.src.includes('?') ? suyo.src.slice(suyo.src.indexOf('?')) : '';

  cargando = new Promise((listo) => {
    const guion = document.createElement('script');

    // **`type="module"` y no un guion normal.** El fichero de MDB es un módulo
    // ES; `dist/js/chart.js` es un shim de tres líneas que lo importa y cuelga
    // `Chart` del global. Cargado como guion normal se ejecutaría sin definir
    // nada y la gráfica esperaría para siempre.
    guion.type = 'module';
    guion.src = `${base}chart.js${sello}`;

    // El `load` de un módulo llega **después** de ejecutarlo, así que a esas
    // alturas el global ya está. El evento es el cinturón por si algún día el
    // shim se vuelve asíncrono.
    const acabar = () => listo(window.Chart || null);

    window.addEventListener('company-chart-listo', acabar, { once: true });
    guion.onload = acabar;
    guion.onerror = () => {
      // **La promesa fallida no se guarda.** Se dejaba en `cargando` y todas
      // las llamadas posteriores recibían ese `null` ya resuelto: un corte de
      // red de un segundo dejaba las gráficas rotas hasta recargar la página,
      // incluidas las que se montaran media hora después.
      cargando = null;
      guion.remove();
      listo(null);
    };
    document.head.appendChild(guion);
  });

  return cargando;
}

/** Una variable CSS del tema, con un color de reserva si no está definida. */
function color(nombre, reserva) {
  const valor = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();

  return valor === '' ? reserva : valor;
}

/**
 * La paleta, en el orden en que se van gastando las series.
 *
 * Se lee cada vez y no una sola al cargar: el tema puede cambiar de claro a
 * oscuro sin recargar la página.
 */
function paleta() {
  return [
    color('--company-primary', '#1a4d8f'),
    color('--company-success', '#2e7d32'),
    color('--company-warning', '#ed6c02'),
    color('--company-danger', '#c62828'),
    color('--company-info', '#0288d1'),
  ];
}

/** Lo que venga en un atributo como JSON, o el valor de reserva. */
function json(elemento, sufijo, reserva) {
  const bruto = elemento.getAttribute(`${ATTR}-${sufijo}`);

  if (!bruto) return reserva;

  try {
    return JSON.parse(bruto);
  } catch (e) {
    // Un JSON roto **no se traga en silencio**: dejaría una gráfica vacía sin
    // que nadie sepa que el problema es la plantilla y no los datos.
    console.error(`[company-grafica] ${sufijo} no es JSON válido:`, e);

    return reserva;
  }
}

/**
 * Los topes del eje cuando la serie no se mueve.
 *
 * Sin esto, una serie de un solo punto -o de varios con el mismo valor, que es
 * lo normal las primeras tardes- deja a Chart.js eligiendo la escala sobre un
 * recorrido de cero, y lo que sale es un eje de 2.250.001,0 a 2.249.999,6 para
 * un precio de dos millones y cuarto. Diez marcas para decir que no ha pasado
 * nada.
 *
 * El margen es **proporcional al valor** y no una cantidad fija: un ±1 sobre dos
 * millones de pesos no se ve, y sobre 309 centavos de dólar por libra es un
 * tercio de la escala.
 *
 * Devuelve `{}` cuando la serie sí tiene recorrido: ahí Chart.js elige mejor que
 * nosotros, que no sabemos cuántas marcas caben.
 */
function limites(series) {
  const valores = series
    .flatMap((s) => s.datos || [])
    .filter((v) => typeof v === 'number' && Number.isFinite(v));

  if (valores.length === 0) return {};

  const menor = Math.min(...valores);
  const mayor = Math.max(...valores);

  if (mayor !== menor) return {};

  // Un 2 % arriba y abajo, y al menos algo cuando el valor es cero -el MeCIC-,
  // que si no el margen también sería cero y volveríamos al mismo sitio.
  const margen = Math.abs(menor) * 0.02 || 1;

  return { suggestedMin: menor - margen, suggestedMax: mayor + margen };
}

/**
 * Un número con separadores colombianos, para las etiquetas y el tooltip.
 *
 * `Intl` y no una función a mano: `2200000` tiene que salir `2.200.000` y
 * `309.65` tiene que salir `309,65`, que es como lo escribe la fuente. Con los
 * decimales que diga la serie, porque redondear la bolsa a entero perdería el
 * movimiento de un día normal.
 */
function comoNumero(valor, decimales) {
  // **Un hueco no es un cero.** `Intl.format(null)` devuelve «0», y en Chart.js
  // un `null` es la forma normal de decir «ese día no hubo dato». Con el tooltip
  // en modo índice, pasar el ratón por un hueco enseñaba «Precio interno: 0».
  if (valor === null || valor === undefined || Number.isNaN(valor)) return 'sin dato';

  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valor);
}

/**
 * Las gráficas vivas.
 *
 * Un `Set` además de la marca en el elemento, porque hacen falta las dos cosas:
 * la marca dice «esta ya está montada» y el conjunto permite **barrer las que se
 * quedaron sin documento**. Sin el barrido, una pantalla que se repinta por red
 * deja un Chart por repintado, cada uno enganchado a `resize` y a su observador,
 * pintando sobre un lienzo que ya no está en ninguna parte.
 */
const montadas = new Set();

class Grafica {
  constructor(elemento) {
    this.elemento = elemento;
    this.chart = null;

    elemento.__companyGrafica = this;
    montadas.add(this);

    this.pintar();
  }

  get lienzo() {
    let canvas = this.elemento.querySelector('canvas');

    if (!canvas) {
      canvas = document.createElement('canvas');
      this.elemento.appendChild(canvas);
    }

    return canvas;
  }

  get series() {
    return json(this.elemento, 'series', []);
  }

  get etiquetas() {
    return json(this.elemento, 'etiquetas', []);
  }

  /**
   * Cambia los datos sin rehacer la gráfica.
   *
   * Es lo que usa el refresco en vivo: rehacerla entera hace que parpadee y
   * pierde la animación, además de tirar el zoom si lo hubiera.
   *
   * @param {{etiquetas: string[], series: Array<{nombre: string, datos: number[]}>}} datos
   */
  actualizar(datos) {
    this.elemento.setAttribute(`${ATTR}-etiquetas`, JSON.stringify(datos.etiquetas || []));
    this.elemento.setAttribute(`${ATTR}-series`, JSON.stringify(datos.series || []));

    if (!this.chart) {
      this.pintar();

      return;
    }

    // **Si el número de series cambia, se rehace.** Se parcheaban los datos por
    // índice, así que al llegar menos series de las que había montadas -incluido
    // el caso natural de `{series: []}` para decir «ya no hay nada»- las viejas
    // se quedaban pintadas con sus datos de antes.
    if ((datos.series || []).length !== this.chart.data.datasets.length) {
      this.destruirElChart();
      this.pintar();

      return;
    }

    if (this.vacia()) {
      this.destruirElChart();
      this.decirQueNoHay();

      return;
    }

    this.chart.data.labels = datos.etiquetas || [];
    (datos.series || []).forEach((serie, i) => {
      this.chart.data.datasets[i].data = serie.datos;
      // Las notas también: si se refrescan los datos y no ellas, el tooltip
      // enseña la hora de registro del punto que había antes en esa posición.
      this.chart.data.datasets[i].companyNotas = serie.notas || [];
    });
    this.chart.update();
  }

  vacia() {
    const series = this.series;

    return series.length === 0 || series.every((s) => !s.datos || s.datos.length === 0);
  }

  /**
   * El aviso de que no hay nada, en vez de unos ejes en blanco.
   *
   * Con el texto que ponga la aplicación: «todavía no se ha consultado» y «no
   * hubo movimiento este mes» son cosas distintas y solo la aplicación sabe cuál
   * es.
   */
  decirQueNoHay() {
    // **No se vacía el contenedor.** Se hacía con `innerHTML = ''`, y eso se
    // llevaba por delante el `<canvas>` de la plantilla: cuando después llegaba
    // `actualizar()` con datos -que en la pantalla del café es lo normal el
    // primer día- la gráfica se dibujaba en un lienzo nuevo **debajo** del
    // texto «todavía no hay datos», que seguía ahí.
    //
    // Ahora el aviso se añade y se quita, y el lienzo solo se esconde.
    const dice = this.elemento.getAttribute(`${ATTR}-vacia`) || 'Todavía no hay datos que enseñar.';

    this.quitarElAviso();

    const aviso = document.createElement('p');

    aviso.className = 'company-grafica-vacia text-muted text-center my-4';
    aviso.textContent = dice;

    this.lienzo.hidden = true;
    this.elemento.appendChild(aviso);
  }

  quitarElAviso() {
    const puesto = this.elemento.querySelector('.company-grafica-vacia');

    if (puesto) puesto.remove();

    this.lienzo.hidden = false;
  }

  /**
   * Suelta el Chart y deja el elemento como estaba.
   *
   * Hace falta de verdad: Chart.js se engancha a `resize` y al observador del
   * contenedor, así que un Chart que nadie destruye sigue vivo con su lienzo
   * fuera del documento. En una pantalla que se repinta por red, uno por
   * repintado.
   */
  destruirElChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  destruir() {
    this.destruirElChart();
    montadas.delete(this);
    delete this.elemento.__companyGrafica;
  }

  pintar() {
    if (this.vacia()) {
      this.decirQueNoHay();

      return;
    }

    // Si venía de estar vacía, el aviso se retira antes de dibujar.
    this.quitarElAviso();

    traerChart().then((Chart) => {
      if (!Chart) {
        // La gráfica no se pudo cargar. Se dice: un hueco callado se lee como
        // «no hay datos», que es exactamente lo contrario de lo que pasa.
        this.elemento.innerHTML =
          '<p class="text-danger text-center my-4">No se pudo cargar la gráfica.</p>';

        return;
      }

      this.montar(Chart);
    });
  }

  montar(Chart) {
    const colores = paleta();
    const decimales = parseInt(this.elemento.getAttribute(`${ATTR}-decimales`) || '0', 10) || 0;
    const tipo = this.elemento.getAttribute(`${ATTR}-tipo`) || 'line';
    const etiquetas = this.etiquetas;
    // Una sola vez: `series` es un getter que vuelve a leer y a parsear el
    // atributo en cada acceso, y aquí se mira tres veces.
    const series = this.series;

    if (this.chart) this.chart.destroy();

    this.chart = new Chart(this.lienzo, {
      type: tipo,
      data: {
        labels: etiquetas,
        datasets: series.map((serie, i) => ({
          label: serie.nombre,
          data: serie.datos,
          borderColor: colores[i % colores.length],
          backgroundColor: colores[i % colores.length],
          borderWidth: 2,
          // Colgadas del dataset y no de una variable de fuera: `actualizar()`
          // rehace los datos y el tooltip tiene que seguir encontrándolas.
          companyNotas: serie.notas || [],
          // Sin puntos gordos: en una serie diaria de seis meses son ciento
          // ochenta bolas y no se ve la línea.
          pointRadius: etiquetas.length > 60 ? 0 : 3,
          tension: 0.2,
          fill: false,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          // Con una sola serie, el título del panel ya dice qué es: la leyenda
          // solo repite ese texto y se come una franja del alto útil.
          legend: { display: series.length > 1 },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${comoNumero(ctx.parsed.y, decimales)}`,
              // **La nota del punto, debajo del valor.**
              //
              // Es lo que separa una gráfica de una tabla dibujada: la etiqueta
              // del eje dice a qué día se refiere el dato y la nota dice cuándo
              // se registró, que no es lo mismo. En una serie que se consulta
              // dos veces al día, un punto puede haberse corregido por la tarde,
              // y sin esto ese día es indistinguible de los demás.
              //
              // Vacía no se enseña: una línea en blanco en el tooltip parece que
              // falta algo.
              afterLabel: (ctx) => {
                const notas = ctx.dataset.companyNotas;
                const nota = notas && notas[ctx.dataIndex];

                return nota ? nota : undefined;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              // Se saltan etiquetas en vez de girarlas: con ciento ochenta
              // fechas giradas no se lee ninguna.
              maxTicksLimit: ETIQUETAS_MAXIMAS,
              autoSkip: true,
            },
            grid: { display: false },
          },
          y: {
            // **Sin empezar en cero**, y es a propósito. Una serie de precios
            // que se mueve un 2 % sobre dos millones sale como una raya recta si
            // el eje arranca en cero: el movimiento, que es lo único que se
            // viene a mirar, desaparece.
            //
            // Pero eso solo vale cuando hay recorrido que enseñar. Con un punto
            // -o con varios iguales, que es lo normal las primeras tardes- el
            // recorrido es cero y Chart.js inventaba una escala absurda: el eje
            // iba de 2.250.001,0 a 2.249.999,6 para un precio de 2.250.000. Diez
            // marcas para decir que no ha pasado nada.
            //
            // `limites()` da entonces un margen proporcional al propio valor, no
            // un ±1 que en pesos no se ve y en centavos de dólar es enorme.
            beginAtZero: false,
            // Aire arriba y abajo cuando sí hay recorrido, para que la línea no
            // se pegue al borde del panel.
            grace: '8%',
            ...limites(series),
            ticks: { callback: (v) => comoNumero(v, decimales) },
          },
        },
      },
    });
  }
}

export function montarGraficas(raiz = document) {
  const ambito = raiz && typeof raiz.querySelectorAll === 'function' ? raiz : document;

  // Primero se barren las que ya no están en el documento. Es donde acaban las
  // de un panel que se repintó por red: el elemento se sustituyó, la gráfica
  // nueva se monta en el nuevo, y la vieja seguiría viva sin que nadie la mire.
  montadas.forEach((grafica) => {
    if (!grafica.elemento.isConnected) grafica.destruir();
  });

  ambito.querySelectorAll(`[${ATTR}]`).forEach((elemento) => {
    if (!elemento.__companyGrafica) new Grafica(elemento);
  });
}

/**
 * La gráfica montada sobre un elemento, para poder actualizarla desde fuera.
 *
 * @returns {?Grafica}
 */
export function graficaDe(elemento) {
  return elemento ? elemento.__companyGrafica || null : null;
}
